import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { anderbhaerresult } from '../../../store/slices/casinoResultsSlice';

// ─── Card Parser ─────────────────────────────────────────────────────────────
// Parses "8SS" → { value: '8', suit: '♠', color: 'black' }
const SUIT_MAP = {
  SS: { symbol: '♠', color: 'black' },
  CC: { symbol: '♣', color: 'black' },
  DD: { symbol: '♦', color: 'red'   },
  HH: { symbol: '♥', color: 'red'   },
};

    const formatMatchTime = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const pad = (n) => String(n).padStart(2, '0');

  const day = pad(d.getDate());
  const month = pad(d.getMonth() + 1);
  const year = d.getFullYear();
  const hours = pad(d.getHours());
  const minutes = pad(d.getMinutes());
  const seconds = pad(d.getSeconds());

  return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
};
const parseCard = (str = '') => {
  if (!str) return { value: '?', suit: '?', color: 'black' };
  const suitKey = str.slice(-2).toUpperCase();
  const value   = str.slice(0, -2);
  const { symbol = '?', color = 'black' } = SUIT_MAP[suitKey] || {};
  return { value, suit: symbol, color };
};

const parseCards = (str = '') =>
  str ? str.split(',').map(s => parseCard(s.trim())) : [];

const buildDisplayData = (resultData) => {
  const raw = resultData?._raw || resultData || {};

  return {
    roundId:    raw.roundId    || resultData?.roundId    || '',
    matchTime:  raw.settledAt  || resultData?.settledAt  || '',
    winner:     raw.winner     || resultData?.winner     || '',
    suit:       raw.suit       || resultData?.suit       || '',      // "HEART"
    oddEven:    raw.parity     || resultData?.parity     || '',      // "EVEN"
    joker:      raw.rank?.replace('CARD_', '') || resultData?.rank?.replace('CARD_', '') || '', 
    jokerCard:  parseCard(raw.middleCard || resultData?.middleCard || ''),
    andarCards: parseCards(raw.andarCards || resultData?.andarCards || ''),
    baharCards: parseCards(raw.baharCards || resultData?.baharCards || ''),
  };
};

// ─── CardBox ──────────────────────────────────────────────────────────────────
const CardBox = ({ value, suit, color = 'red' }) => (
  <Box sx={{
    width: '42px', height: '58px', minWidth: '42px',
    border: '2px solid #f1e6a5', borderRadius: '3px',
    background: '#fff', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.15)', flexShrink: 0,
  }}>
    <Typography sx={{ fontSize: '20px', fontWeight: 700, lineHeight: 1,
      color: color === 'red' ? '#d40000' : '#000', fontFamily: 'Arial' }}>
      {value}
    </Typography>
    <Typography sx={{ fontSize: '18px', lineHeight: 1, marginTop: '-2px',
      color: color === 'red' ? '#d40000' : '#000' }}>
      {suit}
    </Typography>
  </Box>
);

// ─── ResultModal ──────────────────────────────────────────────────────────────
const ResultModal = ({ open, onClose, resultData }) => {
  if (!open || !resultData) return null;

  const d = buildDisplayData(resultData);

  return (
    <Box onClick={onClose} sx={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
      zIndex: 1400, display: 'flex', alignItems: 'flex-start',
      justifyContent: 'center', paddingTop: '40px',
    }}>
      <Box onClick={(e) => e.stopPropagation()} sx={{
        width: '100%', maxWidth: '460px', background: '#fff',
        overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.35)',
      }}>

        {/* Header */}
        <Box sx={{ background: '#2c3d4d', padding: '9px 14px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '15px' }}>
            Andar Bahar Result
          </Typography>
          <Typography onClick={onClose}
            sx={{ color: '#fff', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>
            ✕
          </Typography>
        </Box>

        {/* Round ID + Time */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', padding: '7px 12px' }}>
          <Typography sx={{ fontSize: '11px', color: '#444', fontWeight: 600 }}>
            Round Id: {d.roundId}
          </Typography>
          <Typography sx={{ fontSize: '11px', color: '#444', fontWeight: 600 }}>
            Match Time: {formatMatchTime(d.matchTime)}
          </Typography>
        </Box>

        {/* Cards Section */}
        <Box sx={{ padding: '6px 12px 14px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>

            {/* A / B labels */}
            <Box sx={{ display: 'flex', flexDirection: 'column',
              justifyContent: 'space-around', height: '130px' }}>
              <Typography sx={{ fontSize: '15px', fontWeight: 700, paddingRight: '8px' }}>A</Typography>
              <Typography sx={{ fontSize: '15px', fontWeight: 700, paddingRight: '8px' }}>B</Typography>
            </Box>

            {/* Joker / Middle Card */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '10px', color: '#888', fontWeight: 600 }}>JOKER</Typography>
              <CardBox value={d.jokerCard.value} suit={d.jokerCard.suit} color={d.jokerCard.color} />
            </Box>

            {/* Andar & Bahar card rows */}
            <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {/* Andar Row */}
              <Box sx={{ display: 'flex', gap: '5px', overflowX: 'auto',
                flexWrap: 'nowrap', scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' } }}>
                {d.andarCards.map((card, i) => (
                  <CardBox key={i} value={card.value} suit={card.suit} color={card.color} />
                ))}
              </Box>

              {/* Divider */}
              <Box sx={{ height: '5px', background: '#d7d7d7', borderRadius: '10px',
                margin: '7px 0', overflow: 'hidden' }}>
                <Box sx={{ width: '40%', height: '100%', background: '#222', borderRadius: '10px' }} />
              </Box>

              {/* Bahar Row */}
              <Box sx={{ display: 'flex', gap: '5px', overflowX: 'auto',
                flexWrap: 'nowrap', scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' } }}>
                {d.baharCards.map((card, i) => (
                  <CardBox key={i} value={card.value} suit={card.suit} color={card.color} />
                ))}
              </Box>
            </Box>
          </Box>

          {/* Result Info */}
<Box sx={{ marginTop: '18px', borderTop: '1px solid #ddd', paddingTop: '10px' }}>
  <Box sx={{ width: '90px', margin: '0 auto' }}>
    {[
      ['Winner', d.winner],
      ['Suit', d.suit],
      ['Odd/Even', d.oddEven],
      ['Joker', d.joker],
    ].map(([label, val]) => (
      <Box
        key={label}
        sx={{
          display: 'flex',
        
        }}
      >
        <Typography sx={{ fontSize: '14px', color: '#333' }}>
          {label}:
        </Typography>
        <Typography sx={{ fontSize: '14px', color: '#333' }}>
          <strong>{val}</strong>
        </Typography>
      </Box>
    ))}
  </Box>
</Box>
        </Box>
      </Box>
    </Box>
  );
};

// ─── AndarBaharResult (exported) ──────────────────────────────────────────────
const AndarBaharResult = ({ open, onClose, resultData }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const roundId  = resultData?.roundId;
  const rounds   = useSelector((state) => state.casinoResults.anderbhaer);

  const dispatch = useDispatch();

  useEffect(() => {
    if (open ) {
      dispatch(anderbhaerresult());
    }
  }, [open, dispatch]);

  // open=true means user clicked a history dot → show the modal
  useEffect(() => {
    if (open) setModalOpen(true);
  }, [open]);

  return (
    <>
     

      <ResultModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); onClose?.(); }}
        resultData={resultData}
      />
    </>
  );
};

export default AndarBaharResult;